import { WidgetTheme } from './types'

// Default theme that matches current Tebuto design (Tailwind config)
export const defaultTheme: WidgetTheme = {
    // Primary colors - Tebuto teal/green
    primaryColor: '#00B4A9', // primary-500
    primaryColorLight: '#33c3ba', // primary-400
    primaryColorDark: '#006665', // primary-700
    primaryColorVeryLight: '#E9F9F8', // primary-50

    // Background colors
    backgroundColor: '#ffffff',

    // Text colors
    textPrimary: '#374151', // gray-700
    textSecondary: '#6b7280', // gray-500
    textOnPrimary: '#ffffff',

    // Border and divider colors
    borderColor: '#d1d5db', // gray-300

    // Calendar specific
    calendarSelectedDay: '#00B4A9',
    calendarAvailableDay: '#ccf0ee', // primary-100

    // Font
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
}

// Helper function to generate theme variations
export function generateThemeVariations(baseTheme: Partial<WidgetTheme>): WidgetTheme {
    const theme = { ...defaultTheme, ...baseTheme }

    // Always auto-generate color variations when primary color is provided
    if (baseTheme.primaryColor) {
        // Generate light variations for hover states and backgrounds
        theme.primaryColorLight = baseTheme.primaryColorLight || lightenColor(baseTheme.primaryColor, 30)
        theme.primaryColorDark = baseTheme.primaryColorDark || darkenColor(baseTheme.primaryColor, 20)
        theme.primaryColorVeryLight = baseTheme.primaryColorVeryLight || lightenColor(baseTheme.primaryColor, 95)

        // Set calendar colors based on primary color
        theme.calendarSelectedDay = baseTheme.calendarSelectedDay || baseTheme.primaryColor
        theme.calendarAvailableDay = baseTheme.calendarAvailableDay || lightenColor(baseTheme.primaryColor, 90)
    }

    return theme
}

// Helper functions to manipulate colors
function lightenColor(color: string, percent: number): string {
    const hex = color.replace('#', '')
    const r = Number.parseInt(hex.substring(0, 2), 16)
    const g = Number.parseInt(hex.substring(2, 4), 16)
    const b = Number.parseInt(hex.substring(4, 6), 16)

    const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)))
    const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)))
    const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)))

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

function darkenColor(color: string, percent: number): string {
    const hex = color.replace('#', '')
    const r = Number.parseInt(hex.substring(0, 2), 16)
    const g = Number.parseInt(hex.substring(2, 4), 16)
    const b = Number.parseInt(hex.substring(4, 6), 16)

    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)))
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)))
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)))

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}
